import { Controller } from "@hotwired/stimulus";
import { getSchema } from "../schemas";

export default class extends Controller {
  static values = { schema: String };
  static targets = ["error"];

  connect() {
    this.schema = getSchema(this.schemaValue);
    this.element.addEventListener("submit", this.validateForm);
  }

  disconnect() {
    this.element.removeEventListener("submit", this.validateForm);
  }

  // Validate entire form on submit
  validateForm = (event) => {
    if (!this.schema) return;

    this.clearAllErrors();
    const data = this.collectFormData();
    const result = this.schema.safeParse(data);

    if (!result.success) {
      event.preventDefault();
      event.stopPropagation();
      this.displayErrors(result.error.issues);
    }
  };

  // Validate single field on blur
  validateField(event) {
    if (!this.schema) return;

    const fieldName = this.getFieldName(event.target);
    if (!fieldName) return;

    this.clearFieldErrorByName(fieldName);
    const data = this.collectFormData();
    const result = this.schema.safeParse(data);

    if (!result.success) {
      const fieldIssue = result.error.issues.find(
        (issue) => issue.path[0] === fieldName
      );
      if (fieldIssue) {
        this.showFieldError(fieldName, fieldIssue.message);
      }
    }
  }

  // Clear error when user starts typing/changing
  clearFieldError(event) {
    const fieldName = this.getFieldName(event.target);
    if (fieldName) {
      this.clearFieldErrorByName(fieldName);
    }
  }

  // --- Private helpers ---

  collectFormData() {
    const data = {};
    const formData = new FormData(this.element);

    // Collect fields defined in the schema shape
    const schemaShape = this.schema.shape;
    for (const key of Object.keys(schemaShape)) {
      const value = formData.get(`appointment[${key}]`) || "";
      data[key] = value;
    }

    return data;
  }

  displayErrors(issues) {
    let firstErrorField = null;

    for (const issue of issues) {
      const fieldName = issue.path[0];
      if (!fieldName) continue;

      this.showFieldError(fieldName, issue.message);

      if (!firstErrorField) {
        firstErrorField = fieldName;
      }
    }

    // Scroll to first error
    if (firstErrorField) {
      const errorEl = this.findErrorTarget(firstErrorField);
      if (errorEl) {
        errorEl.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }

  showFieldError(fieldName, message) {
    const errorEl = this.findErrorTarget(fieldName);
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.classList.remove("hidden");
    }

    // Add red border to the input
    const input = this.findInput(fieldName);
    if (input) {
      input.classList.add("border-red-500");
      input.classList.remove("border-gray-300", "border-gray-400");
    }
  }

  clearFieldErrorByName(fieldName) {
    const errorEl = this.findErrorTarget(fieldName);
    if (errorEl) {
      errorEl.textContent = "";
      errorEl.classList.add("hidden");
    }

    const input = this.findInput(fieldName);
    if (input) {
      input.classList.remove("border-red-500");
      // Restore original border based on form context
      const borderClass = input.closest("[data-form-validation-schema-value]")
        ?.dataset.formValidationSchemaValue === "operations-appointment"
        ? "border-gray-400"
        : "border-gray-300";
      if (!input.classList.contains(borderClass)) {
        input.classList.add(borderClass);
      }
    }
  }

  clearAllErrors() {
    this.errorTargets.forEach((el) => {
      el.textContent = "";
      el.classList.add("hidden");
    });

    // Remove red borders from all inputs within this controller
    this.element
      .querySelectorAll(".border-red-500")
      .forEach((el) => {
        el.classList.remove("border-red-500");
      });
  }

  findErrorTarget(fieldName) {
    return this.errorTargets.find(
      (el) => el.dataset.field === fieldName
    );
  }

  findInput(fieldName) {
    return (
      this.element.querySelector(
        `[name="appointment[${fieldName}]"]`
      ) ||
      this.element.querySelector(`[data-field="${fieldName}"]`)
    );
  }

  getFieldName(element) {
    // Try data-field attribute first
    if (element.dataset.field) return element.dataset.field;

    // Extract from name attribute: appointment[field_name]
    const match = element.name?.match(/appointment\[(\w+)\]/);
    return match ? match[1] : null;
  }
}
